import React, { useState, useContext, useCallback } from "react";
import { Highlights } from "components/ScriptureView/ScriptureItems";
import { useScriptureRouting } from "./useScriptureRouting";
import getVerseIdsFromPassageId from "utils/getVerseIdsFromPassageId";
import { isEqual } from "lodash-es";

interface HighlightContext {
  highlights: Highlights;
  setHighlight: (highlightKey: string, verseOrgIds: string[]) => void;
  toggleHighlight: (highlightKey: string, verseOrgIds: string[]) => void;
}

const defaultHighlights = {
  highlighted: [] as string[], // used for showing hover states
  matched: [] as string[], // used for showing matched verse ids in the url (e.g. search result click)
  selected: [] as string[], // used for tracking which verses have been clicked
  feedbacked: [] as string[] // used for tracking which verse has feedback
};

export const ScriptureHighlightsContext = React.createContext<HighlightContext>(
  {
    highlights: defaultHighlights,
    setHighlight: () => {},
    toggleHighlight: () => {}
  }
);

export const ScriptureHighlightsProvider: React.FC = ({ children }) => {
  const { passageId } = useScriptureRouting();
  const [highlights, setHighlights] = useState<Highlights>({
    ...defaultHighlights,
    matched: passageId ? getVerseIdsFromPassageId(passageId) : []
  });

  const setHighlight = useCallback(
    (highlightKey: string, verseOrgIds: string[]) => {
      setHighlights(highlights => ({
        ...highlights,
        [highlightKey]: verseOrgIds
      }));
    },
    []
  );

  const toggleHighlight = useCallback(
    (highlightKey: string, verseOrgIds: string[]) => {
      setHighlights(highlights => {
        const sameVerses = isEqual(highlights[highlightKey], verseOrgIds);
        const newVerseOrgIds = sameVerses ? [] : verseOrgIds;
        return {
          ...highlights,
          [highlightKey]: newVerseOrgIds
        };
      });
    },
    []
  );

  return (
    <ScriptureHighlightsContext.Provider
      value={{
        highlights,
        setHighlight,
        toggleHighlight
      }}
    >
      {children}
    </ScriptureHighlightsContext.Provider>
  );
};

export const useHighlights = () => useContext(ScriptureHighlightsContext);
